import URL from 'url';

import { RefObject, Component, createRef } from 'react';

import qs from 'qs';
import { RouterActionsType } from 'react-resource-router';

import Spinner from '@atlaskit/spinner';

import {
  genPropagateMouseMoveEvent,
  genPropagateMouseUpEvent,
} from 'src/sections/workspace/sections/settings/containers/iframe-wrapped';

import {
  SettingsPageContainer,
  SettingsPageIframe,
  SettingsPageLoadingWrapper,
} from './settings.styled';

type SetIsLoading = (isLoading: boolean) => void;

export type IframeActionProps = {
  event: MessageEvent;
  isLoading: boolean;
  setIsLoading: SetIsLoading;
  iframeRef: RefObject<HTMLIFrameElement>;
};

export type Props = {
  url?: string;
  title?: string;
  showLoadingOnUrlChange?: boolean;
  onIframeLoad?: (setIsLoading: SetIsLoading) => void;
  onIframeSubmit?: (props: IframeActionProps) => void;
  onIframeReadyStateChangeComplete?: (props: IframeActionProps) => void;
  topOffset?: number;
  push?: RouterActionsType['push'];
};

type State = {
  isLoading: boolean;
  loadTimestamp: number;
};

export class SettingsIframe extends Component<Props, State> {
  onMouseMoveIFrame: (event: MouseEvent) => void;
  onMouseUpIFrame: (event: MouseEvent) => void;

  static getIframeURL(str: string) {
    const url = URL.parse(str);
    const query = qs.parse(url.query || '');
    const pageQuery = qs.parse(URL.parse(window.location.href).query || '');
    return URL.format({
      protocol: url.protocol || window.location.protocol,
      host: url.host || window.location.host,
      pathname: url.pathname,
      query: { ...query, ...pageQuery, iframe: true, spa: 0 },
      hash: window.location.hash,
    });
  }

  state = {
    isLoading: true,
    loadTimestamp: Date.now(),
  };

  componentDidMount() {
    window.addEventListener('message', this.onMessage);
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.showLoadingOnUrlChange && prevProps.url !== this.props.url) {
      this.setIsLoading(true);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.onMessage);

    this.iframeRef.current?.contentWindow?.removeEventListener(
      'mousemove',
      this.onMouseMoveIFrame
    );
    this.iframeRef.current?.contentWindow?.removeEventListener(
      'mouseup',
      this.onMouseUpIFrame
    );
  }

  iframeRef = createRef<HTMLIFrameElement>();

  setIsLoading = (isLoading: boolean) => this.setState({ isLoading });
  setLoadTimestamp = () => this.setState({ loadTimestamp: Date.now() });

  getActionProps = (event: MessageEvent) => ({
    event,
    isLoading: this.state.isLoading,
    setIsLoading: this.setIsLoading,
    iframeRef: this.iframeRef,
  });

  onMessage = (evt: MessageEvent) => {
    const {
      onIframeSubmit: onSubmit,
      onIframeReadyStateChangeComplete: onChange,
    } = this.props;
    if (evt.origin !== window.location.origin) {
      return;
    }
    if (evt.data === 'iframe:submit') {
      if (typeof onSubmit === 'function') {
        onSubmit(this.getActionProps(evt));
      } else {
        this.setIsLoading(true);
      }
    } else if (evt.data === 'iframe:readystatechange:complete') {
      if (typeof onChange === 'function') {
        onChange(this.getActionProps(evt));
      } else {
        this.setIsLoading(false);
      }
    }

    // Support client side navigation for iframed pages
    if (this.props.push && evt.data.isNavRequestMessage && evt.data.url) {
      this.setIsLoading(true);
      // This timestamp is to force re-render the component even when URL stays the same (essentially just a reload)
      // This is needed specifically for client-side transitions in legacy issues
      this.setLoadTimestamp();
      this.props.push(evt.data.url);
    }
  };

  render() {
    const { isLoading, loadTimestamp } = this.state;
    const { url, title, onIframeLoad, topOffset } = this.props;
    const iframeUrl = url ? SettingsIframe.getIframeURL(url) : undefined;

    return (
      <SettingsPageContainer topOffset={topOffset}>
        {isLoading && (
          <SettingsPageLoadingWrapper>
            <Spinner size="large" />
          </SettingsPageLoadingWrapper>
        )}
        {iframeUrl && (
          <SettingsPageIframe
            // Forces re-render for all URL changes or reloads on the same URL
            // (re-rendering component adds two history entries when navigating between iframed pages (ex. legacy issues/wiki) and breaks back+forward behavior)
            key={`${loadTimestamp}:${iframeUrl}`}
            id="settings-frame"
            title={title}
            src={iframeUrl}
            isLoading={isLoading}
            onLoad={event => {
              const iframe = event.currentTarget;
              this.onMouseMoveIFrame = genPropagateMouseMoveEvent(iframe);
              this.onMouseUpIFrame = genPropagateMouseUpEvent(iframe);

              iframe.contentWindow?.addEventListener(
                'mousemove',
                this.onMouseMoveIFrame
              );
              iframe.contentWindow?.addEventListener(
                'mouseup',
                this.onMouseUpIFrame
              );
              if (onIframeLoad) {
                onIframeLoad(this.setIsLoading);
              }
            }}
            aria-hidden={isLoading}
            ref={this.iframeRef}
          />
        )}
      </SettingsPageContainer>
    );
  }
}
